/**
 * Client-side accessors for navigation-time config that used to live in
 * `runtimeConfig.public.authRules` / `runtimeConfig.public.showChatRoutes`.
 *
 * Those maps are large (~140 KB combined when serialized) and were inlined
 * into every prerendered HTML page's `window.__NUXT__.config`, bloating the
 * critical-path HTML on every navigation. Neither is read on the initial
 * paint of a prerendered route — route middleware skips on SSR (see
 * `auth.global.ts`) and the chat widget is `<ClientOnly>` — so the data
 * can be lazy-fetched from CDN-served static JSON files emitted alongside
 * `_redirects.json` (see `nitro:build:public-assets` in `nuxt.config.ts`).
 *
 * Mirrors the lazy-fetch pattern used by `aa-redirects.global.ts`. Each
 * fetch fires once per page load, the promise is reused so concurrent
 * consumers don't refetch, and the result is cached for the SPA session.
 */
import type { AuthRule } from "#shared/authRule";

let authRulesPromise: Promise<Record<string, AuthRule>> | null = null;
const showChatRoutesPromises = new Map<string, Promise<string[]>>();

export function getAuthRules(): Promise<Record<string, AuthRule>> {
  if (!authRulesPromise) {
    authRulesPromise = $fetch<Record<string, AuthRule>>(
      "/auth-rules.json",
    ).catch((error) => {
      if (import.meta.dev) {
        console.warn("[nav-config] Failed to load /auth-rules.json", error);
      }
      return {};
    });
  }
  return authRulesPromise;
}

/**
 * Chat-launcher opt-ins for ONE locale. The full list is ~220 KB of route
 * strings; the per-locale static files (emitted by the public-assets hook)
 * are ~25 KB each, and a visitor only ever needs their own locale's slice.
 * Cached per locale for the SPA session — a language switch fetches the new
 * locale's file once.
 */
export function getShowChatRoutes(locale: string): Promise<string[]> {
  const key = /^[a-z]{2}$/.test(locale) ? locale : "en";
  let promise = showChatRoutesPromises.get(key);
  if (!promise) {
    promise = $fetch<string[]>(`/show-chat-routes/${key}.json`).catch(
      (error) => {
        if (import.meta.dev) {
          console.warn(
            `[nav-config] Failed to load /show-chat-routes/${key}.json`,
            error,
          );
        }
        return [];
      },
    );
    showChatRoutesPromises.set(key, promise);
  }
  return promise;
}
